import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useId } from "react";

import { cn } from "@/shared/lib";
import type { Severity } from "@/shared/ui/severity-dot";
import { Skeleton } from "@/shared/ui/skeleton";

import { useHeroLookup } from "../../api/useHeroLookup";
import { useMatches } from "../../api/useMatches";
import { useStatsSummary } from "../../api/useStatsSummary";
import { formatPercent } from "../../lib/formatPercent";
import { heroStats } from "../../lib/heroStats";
import { winRateSeverity } from "../../lib/winRateSeverity";
import { HeroAvatar } from "../HeroAvatar";

// Сколько героев в списке.
const HERO_LIMIT = 6;

const BAR_COLORS: Record<Severity, string> = { good: "bg-good", warn: "bg-warn", bad: "bg-bad" };
const TEXT_COLORS: Record<Severity, string> = {
  good: "text-good",
  warn: "text-warn",
  bad: "text-bad",
};

const GAMES = msg({ message: "{count, plural, one {# match} other {# matches}}" });
const ALL_TIME = msg({
  message: "{count, plural, one {All time, # match.} other {All time, # matches.}}",
});
const RECENT = msg({
  message: "{count, plural, one {For the last # match.} other {For the last # matches.}}",
});

const SKELETON_ROWS = 5;

// Заглушка на месте «Твоих героев», пока грузятся сводка и матчи: заголовок и строки.
const YourHeroesSkeleton = () => (
  <div className="flex flex-col gap-3 rounded-xl border border-border bg-surface p-5">
    <Skeleton className="h-6 w-40" />
    {Array.from({ length: SKELETON_ROWS }, (_, index) => (
      <div key={index} className="flex items-center gap-2.5">
        <Skeleton className="size-8 shrink-0 rounded-lg" />
        <Skeleton className="h-3 grow" />
      </div>
    ))}
  </div>
);

// «Твои герои»: на ком игрок играл чаще всего и доля побед. За всю историю — из сводки
// игрока; пока её нет — по загруженным последним матчам. Полоса — доля побед, черта
// посередине — 50%.
export const YourHeroes = () => {
  const { i18n } = useLingui();
  const headingId = useId();
  const lookup = useHeroLookup();
  const { query, items } = useMatches();
  const stats = useStatsSummary();
  const summary = stats.data?.summary;

  const allTime = summary != null && summary.heroes.length > 0;
  const rows = allTime
    ? summary.heroes.map((hero) => ({ heroId: hero.hero_id, games: hero.matches, wins: hero.wins }))
    : heroStats(items);
  if (rows.length === 0) {
    return query.isPending || stats.isPending ? <YourHeroesSkeleton /> : null;
  }
  const basis = allTime
    ? i18n._({ ...ALL_TIME, values: { count: summary.matches } })
    : i18n._({ ...RECENT, values: { count: items.length } });

  return (
    <section
      aria-labelledby={headingId}
      className="flex flex-col gap-2.5 rounded-xl border border-border bg-surface p-5"
    >
      <h2 id={headingId} className="font-display text-2xl">
        <Trans>Your heroes</Trans>
      </h2>
      <ul className="flex flex-col gap-2.5">
        {rows.slice(0, HERO_LIMIT).map((stat) => {
          const hero = lookup(stat.heroId);
          const winRate = stat.wins / stat.games;
          const severity = winRateSeverity(winRate);
          return (
            <li
              key={stat.heroId}
              data-hero-id={stat.heroId}
              className="grid grid-cols-[36px_minmax(0,1fr)_54px] items-center gap-2.5"
            >
              <HeroAvatar name={hero.name} iconUrl={hero.iconUrl} size={36} />
              <span className="flex min-w-0 flex-col gap-1">
                <span className="text-sm">
                  <span className="font-semibold">{hero.name}</span>{" "}
                  <span className="text-xs text-subtle">
                    {i18n._({ ...GAMES, values: { count: stat.games } })}
                  </span>
                </span>
                <span
                  aria-hidden="true"
                  className="relative block h-2 overflow-hidden rounded bg-surface-raised"
                >
                  <span
                    className={cn("block h-full", BAR_COLORS[severity])}
                    style={{ width: `${String(winRate * 100)}%` }}
                  />
                  <span className="absolute inset-y-0 left-1/2 w-0.5 bg-subtle/60" />
                </span>
              </span>
              <span className={cn("text-right font-mono font-semibold", TEXT_COLORS[severity])}>
                <span className="sr-only">
                  <Trans>wins:</Trans>{" "}
                </span>
                {formatPercent(winRate, i18n.locale)}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-subtle">
        {basis} <Trans>The line in the middle is 50% wins.</Trans>
      </p>
    </section>
  );
};
